import { FinishedRouteAlert } from "@/reducers/messagesSlice"

export type FinishAlertReferenceRoute = {
  driverJid: string
  routeTimestamp: string
  firstStopId: string
  lastStopId: string
}

export function getFinishAlertReferenceRoute(
  details: FinishedRouteAlert,
  driverJid: string,
): FinishAlertReferenceRoute {
  return {
    driverJid: details.expectedRouteDriverJid || driverJid,
    routeTimestamp: details.expectedRouteTimestamp || details.routeTimestamp,
    firstStopId: details.expectedRouteFirstStopId || details.firstStopId,
    lastStopId: details.expectedRouteLastStopId || details.lastStopId,
  }
}

// The route version right before the driver left the order's first stop.
// Orders stamped with a planned pickup (txtango, Platform Science) are often
// loaded earlier, and by the planned time the pickup is off the route, so
// the version at the order's timestamp no longer plans it (nolimit 41%,
// pawtrans 70% of fulfilled orders drew no planned route, 2026-10).
export function getFinishAlertFallbackRouteTimestamp(
  details: FinishedRouteAlert,
  referenceRoute: FinishAlertReferenceRoute,
): string | undefined {
  if (!referenceRoute.firstStopId || !referenceRoute.lastStopId)
    return undefined
  // Microsecond fractions from the backend; keep what Date parses everywhere.
  const start = Date.parse(
    (details.startTimestamp ?? "").replace(/(\.\d{3})\d+/, "$1"),
  )
  if (Number.isNaN(start)) return undefined
  return new Date(start - 1000).toISOString()
}

// The order's legs from the reference route version, or from the fallback
// version when the first plans none of them. With neither, the first result
// is kept so the map still opens with the driver's actual track.
export async function loadOrderRoute<
  T extends { routes: { legs: unknown[] }[] },
>(
  routeTimestamp: string,
  fallbackTimestamp: string | undefined,
  load: (routeTimestamp: string) => Promise<T | undefined>,
): Promise<T | undefined> {
  const route = await load(routeTimestamp)
  if (route?.routes[0]?.legs.length || !fallbackTimestamp) return route

  const fallback = await load(fallbackTimestamp).catch(() => undefined)
  return fallback?.routes[0]?.legs.length ? fallback : route
}
